import React from 'react';
import Select from 'react-select';

const CustomSelect = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  isMulti = false,
  isSearchable = true,
  isClearable = false,
  isDisabled = false,
  isLoading = false,
  className = '',
  error = '',
  label = '',
  required = false,
  menuPosition = 'absolute',
  maxMenuHeight = 300,
  formatOptionLabel,
  ...rest
}) => {
  // Custom styles matching your theme
  const customStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '44px',
      borderColor: error 
        ? '#E30000' 
        : state.isFocused 
          ? '#07B5B5' 
          : '#DDE1E5',
      borderWidth: '1px',
      borderRadius: '8px',
      boxShadow: state.isFocused ? '0 0 0 1px #07B5B5' : 'none',
      backgroundColor: isDisabled ? '#F8F9FA' : '#FFFFFF',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: error ? '#E30000' : '#07B5B5',
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '6px 12px',
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6F7373',
      fontSize: '14px',
    }),
    input: (base) => ({
      ...base,
      color: '#282828',
      fontSize: '14px',
    }),
    singleValue: (base) => ({
      ...base,
      color: '#282828',
      fontSize: '14px',
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#F6FFFF',
      borderRadius: '6px',
      border: '1px solid #A8F3EE',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#07B5B5',
      fontSize: '13px',
      fontWeight: '500',
      padding: '4px 8px',
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#07B5B5',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#07B5B5',
        color: '#FFFFFF',
      },
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '8px',
      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
      border: '1px solid #DDE1E5',
      overflow: 'hidden',
      marginTop: '4px',
      zIndex: 9999,
    }),
    menuList: (base) => ({
      ...base,
      padding: '4px',
      maxHeight: `${maxMenuHeight}px`,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#07B5B5'
        : state.isFocused
          ? '#F6FFFF'
          : '#FFFFFF',
      color: state.isSelected ? '#FFFFFF' : '#282828',
      cursor: 'pointer',
      padding: '10px 12px',
      borderRadius: '6px',
      fontSize: '14px',
      transition: 'all 0.15s ease',
      '&:active': {
        backgroundColor: '#07B5B5',
        color: '#FFFFFF',
      },
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: '#DDE1E5',
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused ? '#07B5B5' : '#6F7373',
      transition: 'all 0.2s ease',
      '&:hover': {
        color: '#07B5B5',
      },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: '#6F7373',
      cursor: 'pointer',
      '&:hover': {
        color: '#E30000',
      },
    }),
    loadingIndicator: (base) => ({
      ...base,
      color: '#07B5B5',
    }),
    noOptionsMessage: (base) => ({
      ...base,
      color: '#6F7373',
      fontSize: '14px',
      padding: '12px',
    }),
    loadingMessage: (base) => ({
      ...base,
      color: '#6F7373',
      fontSize: '14px',
      padding: '12px',
    }),
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray mb-2">
          {label}
          {required && <span className="text-danger ml-1">*</span>}
        </label>
      )}

      {/* Select Component */}
      <Select
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        isMulti={isMulti}
        isSearchable={isSearchable}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isLoading={isLoading}
        styles={customStyles}
        menuPosition={menuPosition}
        maxMenuHeight={maxMenuHeight}
        formatOptionLabel={formatOptionLabel}
        classNamePrefix="custom-select"
        theme={(theme) => ({
          ...theme,
          borderRadius: 8,
          colors: {
            ...theme.colors,
            primary: '#07B5B5',
            primary75: '#A8F3EE',
            primary50: '#F6FFFF',
            primary25: '#F6FFFF',
            danger: '#E30000',
            dangerLight: '#FFE5E5',
            neutral0: '#FFFFFF',
            neutral5: '#F8F9FA',
            neutral10: '#DDE1E5',
            neutral20: '#DDE1E5',
            neutral30: '#6F7373',
            neutral40: '#6F7373',
            neutral50: '#6F7373',
            neutral60: '#282828',
            neutral70: '#282828',
            neutral80: '#282828',
            neutral90: '#282828',
          },
        })}
        {...rest}
      />

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-danger flex items-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
};

export default CustomSelect;
